import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, PlayCircle, RotateCw, X, RefreshCcw, AlertTriangle, FileText, Copy, Check } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RunStatusBadge } from "./RunBadges";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}
function dur(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

const TL = {
  PENDING: "bg-slate-500", RUNNING: "bg-amber-500",
  SUCCESS: "bg-emerald-500", FAILED: "bg-rose-500", CANCELLED: "bg-neutral-500",
};

function Timeline({ logs = [] }) {
  if (!logs.length) return <div className="text-xs text-white/40 italic">Belum ada aktivitas.</div>;
  return (
    <ol className="relative border-l border-white/10 ml-3 space-y-4" data-testid="run-timeline">
      {logs.map((log, idx) => (
        <li key={idx} className="ml-4">
          <div className={`absolute -left-[7px] w-3 h-3 rounded-full ${TL[log.status] || "bg-white/20"}`} data-testid={`run-tl-dot-${idx}`} />
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-wider text-white/70">{log.status}</span>
            {log.phase && <span className="text-[9px] uppercase tracking-widest text-emerald-300/70 font-bold">{log.phase}</span>}
            <span className="text-[10px] text-white/40 font-mono">{fmt(log.at)}</span>
          </div>
          <div className="text-xs text-white/80 mt-0.5" data-testid={`run-tl-msg-${idx}`}>{log.message}</div>
        </li>
      ))}
    </ol>
  );
}

function ArtifactViewer({ artifacts }) {
  const files = Object.keys(artifacts || {}).sort();
  const [active, setActive] = useState(files[0] || null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (files.length && !active) setActive(files[0]);
    if (active && !files.includes(active)) setActive(files[0] || null);
  }, [files, active]);

  if (!files.length) {
    return <div className="text-xs text-white/40 italic p-4">Belum ada artifact — execute run untuk generate.</div>;
  }

  const content = artifacts[active] || "";
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success(`${active} disalin ke clipboard.`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy gagal."); }
  };

  return (
    <div data-testid="run-artifacts">
      <div className="flex flex-wrap gap-1 mb-3 border-b border-white/10 pb-2">
        {files.map((name) => (
          <button
            key={name}
            onClick={() => setActive(name)}
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-mono font-bold border transition ${
              active === name
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : "bg-white/5 text-white/60 border-white/10 hover:text-white hover:bg-white/10"
            }`}
            data-testid={`run-artifact-tab-${name}`}
          >
            <FileText size={11} className="inline mr-1" /> {name}
          </button>
        ))}
      </div>
      <div className="relative">
        <button
          onClick={doCopy}
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-[10px] font-bold border border-white/10"
          data-testid="run-artifact-copy"
        >
          {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
        </button>
        <pre
          className="text-[11px] font-mono text-emerald-100/90 bg-black/50 border border-white/10 rounded-xl p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all"
          data-testid={`run-artifact-content-${active}`}
        >
{content}
        </pre>
      </div>
    </div>
  );
}

export default function DeploymentRunDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, ts] = await Promise.all([
        api.get(`/platform/deployment-runner/${id}`),
        api.get("/platform/tenants"),
      ]);
      setR(pr.data);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/platform/deployment-runner/${id}/${verb}`, body);
      toast.success(`Run ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  if (loading || !r) {
    return (
      <PlatformLayout title="Deployment Run Detail">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      </PlatformLayout>
    );
  }

  const tenant = tenants.find((t) => t.tenant_id === r.tenant_id);
  const canExec = r.status === "PENDING";
  const canRetry = r.status === "FAILED";
  const canCancel = ["PENDING", "RUNNING"].includes(r.status);

  return (
    <PlatformLayout title={r.run_number}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/deployments/runs")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="run-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          {canExec && (
            <>
              <Button onClick={() => act("execute", { simulate: "success" })} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="run-detail-execute">
                <PlayCircle size={14} className="mr-1" /> Execute (Success)
              </Button>
              <Button onClick={() => act("execute", { simulate: "fail", error_message: "simulated failure" })} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="run-detail-execute-fail">
                <PlayCircle size={14} className="mr-1" /> Execute (Fail sim)
              </Button>
            </>
          )}
          {canRetry && (
            <Button onClick={() => act("retry")} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="run-detail-retry">
              <RotateCw size={14} className="mr-1" /> Retry
            </Button>
          )}
          {canCancel && (
            <Button onClick={() => {
              const reason = window.prompt("Alasan cancel?") || "";
              act("cancel", { reason });
            }} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="run-detail-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {r.status === "FAILED" && r.error_message && (
        <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="run-error-message">
          <AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-rose-200 font-black text-sm">Deployment Run Gagal</div>
            <div className="text-xs text-rose-100/80 mt-1 font-mono break-words">{" "}{r.error_message}</div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-white font-display font-black text-lg" data-testid="run-detail-number">{r.run_number}</div>
        <RunStatusBadge status={r.status} />
        <span className="text-xs text-white/50 font-mono">·  {tenant?.tenant_name || r.tenant_id.slice(0, 8)}</span>
        <span className="ml-auto text-[10px] text-white/40">Started {fmt(r.started_at)} · Finished {fmt(r.finished_at)} · Duration {dur(r.duration_seconds)}</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-3">Timeline</div>
          <Timeline logs={r.logs || []} />
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-3">Artifact Bundle · <span className="text-emerald-300/70">Foundation blueprint</span></div>
          <ArtifactViewer artifacts={r.artifact_bundle || {}} />
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/deployments/runs" className="text-xs text-white/50 hover:text-white underline">← Semua deployment runs</Link>
      </div>
    </PlatformLayout>
  );
}
